import MediaDeviceInfoWithState from "./MediaDeviceInfoPlus";
import {Config, Input} from "../contexts/Configuration";
import {createAudioContext} from "../core/TextToSpeechUtilsPoly";
import {AudioInputHandler} from "../core/AudioInputHandler";

/**
 * MediaDevicesUtils - Singleton for managing audio input/output devices
 *
 * ## Disconnected Device Persistence
 *
 * This class maintains disconnected devices in the device list so users can see
 * which device was previously selected, even after it's unplugged. This is important
 * for user experience as it:
 * - Shows the user what device they had configured
 * - Allows automatic reconnection when the device is plugged back in
 * - Persists across page reloads and navigation
 *
 * ### How it works:
 *
 * 1. **Device caching**: When a device is selected, it's saved to localStorage
 *    via `rememberSelectedDevice()` / `rememberSelectedOutputDevice()` with:
 *    - `deviceId`, `label`, `groupId`, `kind`
 *    - `lastSeen`: timestamp of last connection
 *    - `preselected`: flag for auto-selection on load
 *    - `lastPanelId`: which panel it belongs to (for outputs)
 *
 * 2. **On enumerate** (`enumerateDevices()`):
 *    - Compares API devices with cached devices
 *    - If cached device NOT in API list → added to device list with `isActive = false`
 *    - If cached device IS in API list → marked as `isActive = true`
 *    - Devices disconnected for more than 10 days are automatically forgotten
 *
 * 3. **Change detection**: Broadcasts `mediadevices-changed` event with:
 *    - `newDevices`: devices appearing for the first time
 *    - `disconnectedDevices`: devices that went from active → inactive
 *    - `reconnectedDevices`: devices that went from inactive → active
 *
 * 4. **Visual feedback** (handled by DeviceSelector):
 *    - Inactive devices show "(inactive)" prefix
 *    - CSS class `device-selected-disconnected` for styling
 *
 * @see DeviceSelector - UI component that displays devices
 * @see MediaDeviceInfoWithState - device model with `isActive` flag
 */
class MediaDevicesUtils {

    private AVAILABLE_DEVICES_POLL_INTERVAL_TIME = 3000; // ms
    private static TEN_DAYS = 1000 * 60 * 60 * 24 * 10;

    private _initOnce: boolean;
    private _havePermissions: boolean;
    private inputDevices: MediaDeviceInfoWithState[] = [];
    private outputDevices: MediaDeviceInfoWithState[] = [];

    //InputStreamIntervenidos
    private intervenedInputStreams: { [k: string]: AudioInputHandler } = {};
    //deviceId,inputStreams[]


    private streamByDevices: { [k: string]: MediaStream } = {};
    private currentPermissions: string = "TODO"

    private static _instance: MediaDevicesUtils;
    private availableDevicesPollTimer: NodeJS.Timeout | null = null;

    private constructor() {
        this._initOnce = false;
        this._havePermissions = false;
    }

    public static instance(): MediaDevicesUtils {
        if (!MediaDevicesUtils._instance) {
            MediaDevicesUtils._instance = new MediaDevicesUtils();
        }
        return MediaDevicesUtils._instance;
    }

    public async init(): Promise<boolean> {
        if (this._initOnce) {
            return Promise.resolve(true);
        }

        this._initOnce = true;

        try {

            return this.enumerateDevices()
                .then(ok => {
                    console.debug("enumerateDevices ok? ", ok)

                    if (ok && this.supportsDeviceChangeEvent()) {
                        navigator.mediaDevices.addEventListener('devicechange', () => this.enumerateDevices());
                    } else {
                        console.log("[MediaDevices] Browser does not support automatic device change detection. Polling every", this.AVAILABLE_DEVICES_POLL_INTERVAL_TIME, " ms...")
                        if (!this.availableDevicesPollTimer) {
                            this.availableDevicesPollTimer = setInterval(
                                () => this.enumerateDevices(),
                                this.AVAILABLE_DEVICES_POLL_INTERVAL_TIME);
                        }
                    }
                    return ok;
                });
        } catch (e) {
            throw new Error("Unable to access devices.");
        }
    }

    public stop() {
        if (this.availableDevicesPollTimer) {
            clearInterval(this.availableDevicesPollTimer);
            this.availableDevicesPollTimer = null;
        }

        this.freeAllAudioInputHandlers()
    }

    public async promptPermissions() {
        return await navigator.mediaDevices.getUserMedia({audio: true, video: false})
            .then(stream => {
                stream.getTracks().forEach(track => track.stop());
                return true;
            })
    }

    public rememberSelectedDevice(mediaDevice: MediaDeviceInfoWithState, shouldUpdateLocalD: boolean) {
        mediaDevice.lastSeen = Date.now();
        mediaDevice.preselected = true;

        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        cache = {...cache, inputDeviceConfig: mediaDevice};

        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        if (shouldUpdateLocalD) {
            this.inputDevices = this.inputDevices.map(d => {
                if (d.deviceId === mediaDevice.deviceId) {
                    return mediaDevice;
                } else {
                    d.preselected = false;
                    return d;
                }
            })
        }
    }

    public rememberSelectedOutputDevice(mediaDevice: MediaDeviceInfoWithState, panelId: string, shouldUpdateLocalD: boolean) {
        mediaDevice.lastSeen = Date.now();
        mediaDevice.preselected = true;//preselectedDevice.preselected;
        mediaDevice.lastPanelId = panelId;

        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        const preSelectedOutputDevices = cache.outputDevicesConfig || {};
        preSelectedOutputDevices[panelId] = mediaDevice

        cache = {...cache, outputDevicesConfig: preSelectedOutputDevices};

        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        if (shouldUpdateLocalD) {
            this.outputDevices = this.outputDevices.map(d => {
                if (d.deviceId === mediaDevice.deviceId) {
                    return mediaDevice;
                } else {
                    d.preselected = false;
                    return d;
                }
            })
        }
    }

    public forgetSelectedDevice() {
        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
        // if (mediaDevice.kind === 'audioinput') {
        cache = {...cache, inputDeviceConfig: null};
        // }

        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        this.inputDevices = this.inputDevices.map(d => {
            d.preselected = false;
            return d;
        });
    }

    public forgetSelectedOutputDevice(panelId: string) {
        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        // if (mediaDevice.kind === 'audiooutput') {
        const preSelectedOutputDevices = cache.outputDevicesConfig || {};
        delete preSelectedOutputDevices[panelId];
        cache = {...cache, outputDevicesConfig: preSelectedOutputDevices};
        // }
        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        this.outputDevices = this.outputDevices.map(d => {
            d.preselected = false;
            return d;
        });
    }

    private getRememberedDeviceFromStorage():
        { inputDevice: MediaDeviceInfoWithState | null, outputDevices: MediaDeviceInfoWithState[] } {

        const cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        const inputDevicesConfig = cache.inputDeviceConfig || null;
        const outputDevicesConfig = cache.outputDevicesConfig || {};

        if (inputDevicesConfig) {
            inputDevicesConfig.preselected = true;
        }

        // console.log('getRememberedDeviceFromStorage', outputDevicesConfig);

        let justOutputDevices: MediaDeviceInfoWithState[] = [];
        for (const key in outputDevicesConfig) { // idem a hacer [...myMap.values()]
            outputDevicesConfig[key].preselected = true;
            outputDevicesConfig[key].lastPanelId = key;
            // console.log('getRememberedDeviceFromStorage', key, outputDevicesConfig[key]);
            justOutputDevices.push(outputDevicesConfig[key]);
        }

        return {
            outputDevices: justOutputDevices,
            inputDevice: inputDevicesConfig
        };
    }

    public async enumerateDevices() {
        console.log("Enumerating devices... havePermissions ", this._havePermissions);

        if (!this._havePermissions) {
            await this.promptPermissions()
                .then(_p => {
                    console.debug("promptPermissions...", this._havePermissions);
                    this._havePermissions = true;
                })
                .catch((e) => {
                    console.error("promptPermissions...", e);

                    // @ts-ignore
                    if (e?.name !== "NotFoundError")
                        throw e;
                });
        }

        return await navigator.mediaDevices.enumerateDevices()
            .then(apiDevices => {
                let newInputDevices: MediaDeviceInfoWithState[] = [];
                let newOutputDevices: MediaDeviceInfoWithState[] = [];

                // Store references to the current device lists for comparison
                const oldInputDevices = this.inputDevices.map(d => this.copyDevice(d));
                const oldOutputDevices = this.outputDevices.map(d => this.copyDevice(d));

                const cachedDevices = this.getRememberedDeviceFromStorage();
                const cachedInputDevice = cachedDevices.inputDevice;
                const cachedOutputDevices = cachedDevices.outputDevices;

                const validDevices = apiDevices
                    .filter(d => d.kind === 'audioinput' || d.kind === 'audiooutput')
                    .filter(d => d.deviceId !== '')
                    .filter(d => d.deviceId !== 'default');

                // console.log("[0] apiDevices ", apiDevices);
                // console.log("[1] validDevices ", validDevices, cachedInputDevice, cachedOutputDevices);

                // Put API devices
                let found = false;
                let foundOutputs: string[] = []

                validDevices.forEach(d => {
                    if ('audioinput' === d.kind) {
                        let mediaDeviceInfoWithState = new MediaDeviceInfoWithState(d);
                        if (cachedInputDevice && d.deviceId === cachedInputDevice.deviceId) {
                            found = true;
                            // mediaDeviceInfoWithState.lastSeen = Date.now();
                            // mediaDeviceInfoWithState.preselected = true;
                            this.rememberSelectedDevice(mediaDeviceInfoWithState, false);
                        }

                        newInputDevices.push(mediaDeviceInfoWithState);
                    } else if ('audiooutput' === d.kind) {
                        const preselectedDevice = cachedOutputDevices.find(c => c.deviceId === d.deviceId);
                        let mediaOutputDeviceInfoWithState = new MediaDeviceInfoWithState(d);
                        // console.log('--> found', preselectedDevice, mediaOutputDeviceInfoWithState)
                        if (preselectedDevice) {
                            foundOutputs.push(mediaOutputDeviceInfoWithState.deviceId);
                            mediaOutputDeviceInfoWithState.lastSeen = Date.now();
                            // mediaOutputDeviceInfoWithState.preselected = preselectedDevice.preselected;
                            // mediaOutputDeviceInfoWithState.lastPanelId = preselectedDevice.lastPanelId;
                            this.rememberSelectedOutputDevice(mediaOutputDeviceInfoWithState, preselectedDevice.lastPanelId, false);
                        }

                        newOutputDevices.push(mediaOutputDeviceInfoWithState);
                    }
                })

                // === DISCONNECTED INPUT DEVICE PERSISTENCE ===
                // If cached input device was NOT found in current API devices, keep it visible
                // as "inactive" so user can see what was previously configured.
                // Device is forgotten after 10 days of being disconnected.
                if (!found && cachedInputDevice) {
                    if ((Date.now() - cachedInputDevice.lastSeen) > MediaDevicesUtils.TEN_DAYS) {
                        this.forgetSelectedDevice();
                    } else {
                        cachedInputDevice.isActive = false; // Mark as disconnected
                        newInputDevices.push(cachedInputDevice); // Keep in list for visibility
                    }
                }

                // === DISCONNECTED OUTPUT DEVICES PERSISTENCE ===
                // Same logic for output devices - keep disconnected devices visible
                // so users know which device was configured for each panel.
                if (cachedOutputDevices.length > 0) {
                    cachedOutputDevices.forEach(d => {
                        if (!foundOutputs?.includes(d.deviceId)) {
                            if ((Date.now() - d.lastSeen) > MediaDevicesUtils.TEN_DAYS) {
                                this.forgetSelectedOutputDevice(d.lastPanelId);
                            } else {
                                d.isActive = false; // Mark as disconnected
                                newOutputDevices.push(d); // Keep in list for visibility
                            }
                        }
                    })
                }

                // Compare old devices vs new devices and find the changes
                const changes = {
                    newDevices: [] as string[], // new devices
                    reconnectedDevices: [] as string[], // devices that we've already seen but now reconnected
                    disconnectedDevices: [] as string[] // devices that got disconnected
                };

                // Check for new input devices or devices with changed isActive status
                // console.log("newInputDevices 3", newInputDevices);
                newInputDevices.forEach(newDevice => {
                    const oldDevice = oldInputDevices.find(d => d.deviceId === newDevice.deviceId) as MediaDeviceInfoWithState | undefined;
                    let newDeviceId = newDevice.deviceId;
                    if (!oldDevice) {
                        // This is a new device
                        changes.newDevices.push(newDeviceId);
                    } else if (oldDevice.isActive && !newDevice.isActive) {
                        // This device changed to inactive
                        changes.disconnectedDevices.push(newDeviceId);
                    } else if (!oldDevice.isActive && newDevice.isActive) {
                        // This device changed to active
                        changes.reconnectedDevices.push(newDeviceId);
                    }
                });

                oldInputDevices
                    .filter(oldD => !newInputDevices.find(newD => newD.deviceId === oldD.deviceId))
                    .forEach(oldD => {
                        oldD.isActive = false
                        // newInputDevices.push(oldD)
                        changes.disconnectedDevices.push(oldD.deviceId);
                    })

                // console.log("Check CHANGES - newOutputDevices", newOutputDevices)
                // console.log("Check CHANGES - oldOutputDevices", oldOutputDevices)

                // Check for new output devices or devices with changed isActive status
                newOutputDevices.forEach(newDevice => {
                    const oldDevice = oldOutputDevices.find(d => d.deviceId === newDevice.deviceId) as MediaDeviceInfoWithState | undefined;
                    let newDeviceId = newDevice.deviceId;

                    // console.log("Check CHANGES ", newDevice, oldDevice); //FIXME - aca queda raro, el output device queda como newDevices en lugar de reconnectedDevices
                    if (!oldDevice) {
                        // This is a new device
                        changes.newDevices.push(newDeviceId);
                    } else if (oldDevice.isActive && !newDevice.isActive) {
                        // This device changed to inactive
                        changes.disconnectedDevices.push(newDeviceId);
                    } else if (!oldDevice.isActive && newDevice.isActive) {
                        // This device changed its active
                        changes.reconnectedDevices.push(newDeviceId);
                    }
                });

                // Update the device lists
                this.inputDevices = newInputDevices;
                this.outputDevices = newOutputDevices;

                // console.log("Enumerated input devices: ", this.inputDevices, "Enumerated output devices: ", this.outputDevices);
                // console.log("Device changes: ", changes);

                if (changes.disconnectedDevices.length > 0) {
                    changes.disconnectedDevices.forEach(deviceId => this.freeAudioHandler(deviceId));
                }

                const haveChanges = (changes.disconnectedDevices.length > 0) || (changes.newDevices.length > 0) || (changes.reconnectedDevices.length > 0);
                if (haveChanges) {
                    window.postMessage({
                        type: 'mediadevices-changed',
                        changes: changes
                    }, '*');
                }
                return validDevices.length > 0;
            })
            .catch(e => {
                console.error(e);
                throw e;
            })
    }

    private creatingByDevice: { [k: string]: Promise<MediaStream> } = {};

    public getInputDeviceStream(deviceId: string, gain: number) {

        const reuse = () => {
            const handler = this.intervenedInputStreams[deviceId];
            if (handler) {
                const ms = handler.getAudioStream();
                const isLive = ms.getAudioTracks().some(t => t.readyState === 'live');
                if (isLive) {
                    // Ensure gain changes get applied when reusing existing handler
                    handler.setVolume(gain);
                    return Promise.resolve(ms);
                }
                // If not live, free it before recreating
                this.freeAudioHandler(deviceId);
            }
            return null;
        }

        const reused = reuse();
        if (reused) return reused;


        if (!this.creatingByDevice[deviceId]) {
            this.creatingByDevice[deviceId] = navigator.mediaDevices.getUserMedia({
                audio: { deviceId: { exact: deviceId }, autoGainControl: false }
            }).then(stream => {
                const newHandler = new AudioInputHandler(deviceId, gain, stream);
                this.intervenedInputStreams[deviceId] = newHandler;
                // return newHandler.getAudioStream();

                const ms = newHandler.getAudioStream();
                const isLive = ms.getAudioTracks().some(t => t.readyState === 'live');
                if (isLive) {
                    // Ensure gain changes get applied
                    newHandler.setVolume(gain);
                }
                return ms;
            }).catch(err => {
                console.error('[MediaDevicesUtils] getUserMedia failed for', deviceId, err.name, err.message);
                throw err;

            }).finally(() => { delete this.creatingByDevice[deviceId]; });
        }

        return this.creatingByDevice[deviceId];

    }

    public getAudioInputHandler(deviceId: string): AudioInputHandler {
        return this.intervenedInputStreams[deviceId];
    }

    public freeAllAudioInputHandlers() {
        Object.keys(this.intervenedInputStreams).forEach(deviceId => {
            try {
                this.intervenedInputStreams[deviceId]?.stop();
            } catch {
            }
            delete this.intervenedInputStreams[deviceId];
        });
    }

    public freeAudioHandler(deviceId: string) {
        try {
            this.intervenedInputStreams[deviceId]?.stop();
        } catch {
        }
        delete this.intervenedInputStreams[deviceId];
    }

    public getDevices(kind?: MediaDeviceKind): MediaDeviceInfoWithState[] {
        if ('audioinput' === kind) {
            return this.inputDevices;
        } else if ('audiooutput' === kind) {
            return this.outputDevices;
        }

        return [...this.inputDevices, ...this.outputDevices];
    }

    supportsDeviceChangeEvent(): boolean {
        return typeof navigator.mediaDevices?.ondevicechange !== 'undefined'
            && typeof navigator.mediaDevices?.addEventListener !== 'undefined';
    }


    public copyDevice(d: MediaDeviceInfoWithState): MediaDeviceInfoWithState {
        const clone = new MediaDeviceInfoWithState({
            deviceId: d.deviceId,
            groupId: d.groupId,
            kind: d.kind,
            label: d.label,
        } as MediaDeviceInfo);

        // copy mutable state
        clone.isActive = d.isActive;
        clone.preselected = d.preselected;
        clone.lastPanelId = d.lastPanelId;
        clone.lastSeen = d.lastSeen;

        return clone;
    }

}

export default MediaDevicesUtils;
